import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import StatCard from "components/ui/StatCard";
import Icon from "components/AppIcon";
import ConfirmDialog from "components/ui/ConfirmDialog";
import VendorTable from "./components/VendorTable";
import LoadingSkeleton from "./components/LoadingSkeleton";
import AddVendorModal from "./components/AddVendorModal";
import { useGetVendors } from "./useGetVendors";
import { useCreateVendor } from "./useCreateVendor";
import { useUpdateVendor } from "./useUpdateVendor";
import { useDeleteVendor } from "./useDeleteVendor";
import { mapFormToVendorPayload, mapVendorToForm } from "./logic/mapVendor";
import { sumOpeningBalanceByType } from "./logic/stats";

const VendorManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const { data: vendors = [], isLoading } = useGetVendors();

  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const deleteVendor = useDeleteVendor();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal + delete state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [deletingVendor, setDeletingVendor] = useState(null);

  // Reset paging when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = vendors.filter((v) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      v.name?.toLowerCase().includes(term) ||
      v.email?.toLowerCase().includes(term) ||
      v.phone?.toLowerCase().includes(term) ||
      v.vatNumber?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ── Handlers ────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingVendor(null);
    setModalOpen(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(mapVendorToForm(vendor));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingVendor(null);
  };

  const handleSave = (formData) => {
    const payload = mapFormToVendorPayload(formData);

    if (formData.id) {
      updateVendor.mutate(
        { id: formData.id, ...payload },
        { onSuccess: closeModal },
      );
    } else {
      createVendor.mutate(payload, { onSuccess: closeModal });
    }
  };

  const confirmDelete = () => {
    if (!deletingVendor) return;
    deleteVendor.mutate(deletingVendor.id || deletingVendor._id, {
      onSuccess: () => setDeletingVendor(null),
    });
  };

  const isSaving = createVendor.isPending || updateVendor.isPending;

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = [
    {
      label: t("vendors.stats.total"),
      value: vendors.length,
      icon: "Truck",
      color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
    },
    {
      label: t("vendors.stats.active"),
      value: vendors.length,
      icon: "CheckCircle2",
      color:
        "text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
    },
    {
      label: t("vendors.stats.youOwe"),
      value: `SR ${sumOpeningBalanceByType(vendors, "you_owe").toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
      icon: "TrendingDown",
      color: "text-red-500 bg-red-100 dark:bg-red-900/20 dark:text-red-400",
    },
    {
      label: t("vendors.stats.vendorsOweYou"),
      value: `SR ${sumOpeningBalanceByType(vendors, "owes_you").toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
      icon: "TrendingUp",
      color:
        "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400",
    },
  ];

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("vendors.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("vendors.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("actions.export")}
              </Button>
              <Button
                variant="default"
                size="md"
                iconName="Plus"
                onClick={openAdd}
              >
                {t("vendors.addVendor", "Add Vendor")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* ── Stats Row ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </motion.div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card rounded-xl p-5 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("vendors.searchPlaceholder")}
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={15} />
              </button>
            )}
          </div>
        </motion.div>

        {/* ── Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <VendorTable
            vendors={paginated}
            onEdit={openEdit}
            onDelete={(vendor) => setDeletingVendor(vendor)}
          />
        </motion.div>

        {/* ── Empty State ── */}
        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-4"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Truck" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("vendors.emptyTitle")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm
                ? t("common.adjustSearch")
                : t("vendors.emptyDescription")}
            </p>
          </motion.div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t("common.showingRange", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, filtered.length),
                total: filtered.length,
                entity: t("entities.vendors"),
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              <span className="text-sm text-foreground px-2">
                {t("common.pageOf", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </div>
          </div>
        )}
      </main>

      {/* ── Add / Edit Modal ── */}
      <AnimatePresence>
        {modalOpen && (
          <AddVendorModal
            existingVendor={editingVendor}
            onSave={handleSave}
            onClose={closeModal}
            isSubmitting={isSaving}
          />
        )}
      </AnimatePresence>

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        isOpen={!!deletingVendor}
        title={t("vendors.deleteTitle", "Delete vendor?")}
        message={t("vendors.deleteMessage", {
          name: deletingVendor?.name || "",
          defaultValue: `This will remove ${deletingVendor?.name || "this vendor"}. This cannot be undone.`,
        })}
        isLoading={deleteVendor.isPending}
        onConfirm={confirmDelete}
        onClose={() => setDeletingVendor(null)}
      />
    </div>
  );
};

const VendorManagement = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <VendorManagementContent />
    </div>
  </SidebarProvider>
);

export default VendorManagement;
